import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, debounceTime, map, Observable, skip, takeUntil, tap } from 'rxjs';
import { CoreModule, DestroyableComponent, IvvTitleComponent, OffersType, ProductType } from '@toevla-webapplications/core';
import { Store } from '@ngrx/store';
import { IvvaStore } from '../../store/store.model';
import { TabbarComponent } from './components/tabbar/tabbar.component';
import { MainFilterComponent } from './components/main-filter/main-filter.component';
import { SideFilterComponent } from './components/side-filter/side-filter.component';
import { OfferCardComponent } from './components/offer-card/offer-card.component';
import { Offer } from '../../services/offers/offer.model';
import OFFERS_ACTIONS from '../../store/offers/offers.actions';
import { OffersService } from '../../services/offers/offers.service';
import { OffersFilters, OffersOrder } from '../../store/offers/offers.model';
import { ToastsService } from '../../services/toasts/toasts.service';
import { PriceFilterComponent } from './components/price-filter/price-filter.component';
import { MapComponent } from '../../components/map/map.component';
import { NewsletterComponent } from '../../components/newsletter/newsletter.component';
import { FavoritesService } from '../../services/favorites/favorites.service';
import { HelpComponent } from '../../components/help/help.component';
import { environment } from '../../../environments/env.uat';

@Component({
	selector: 'ivva-home',
	imports: [
		CommonModule,
		TranslateModule,
		TabbarComponent,
		MainFilterComponent,
		SideFilterComponent,
		OfferCardComponent,
		IvvTitleComponent,
		CoreModule,
		PriceFilterComponent,
		MapComponent,
		NewsletterComponent,
		HelpComponent,
	],
	templateUrl: './home.component.html',
	styleUrl: './home.component.scss',
})
export class HomeComponent extends DestroyableComponent implements OnInit {
	favorites$: Observable<number>;
	loading$: Observable<boolean>;
	nextAvailable$: Observable<boolean>;
	offers$: Observable<Offer[]>;
	selectedOrder$: Observable<OffersOrder>;
	sideFilters$: Observable<Filtertje[]>;
	showFilters = signal(false);
	showDeLijn$: Observable<boolean>;
	showKampas$: Observable<boolean>;
	total$: Observable<number>;
	type$: Observable<OffersType>;
	view$: Observable<'list' | 'map'>;

	readonly OffersType = OffersType;
	readonly RRN_APP_URL = environment.rrnAppUrl;

	private readonly favoritesService = inject(FavoritesService);
	private readonly offersService = inject(OffersService);
	private readonly store = inject(Store<IvvaStore>);
	private readonly toastsService = inject(ToastsService);
	private readonly ORDERS: OffersOrder[] = [
		{ name: 'ALPHABETICALLY_ASC', asc: true, sort: 'name' },
		{ name: 'ALPHABETICALLY_DESC', asc: false, sort: 'name' },
		{ name: 'PRICE_ASC', asc: true, sort: 'price' },
		{ name: 'PRICE_DESC', asc: false, sort: 'price' },
	];

	get orders$(): Observable<OffersOrder[]> {
		return this.type$.pipe(
			map((type) => {
				if (type === OffersType.ORGANIZED_HOLIDAY) {
					return this.ORDERS.slice(0, 2);
				}

				return this.ORDERS;
			})
		);
	}

	ngOnInit() {
		this.loading$ = this.store.select('offers', 'data', 'loading');
		this.nextAvailable$ = this.store.select('offers', 'data', 'nextAvailable');
		this.offers$ = this.store.select('offers', 'data', 'list');
		this.selectedOrder$ = this.store.select('offers', 'order', 'name');
		this.sideFilters$ = this.store.select('offers').pipe(map(({ type, filters }) => this.activeFiltersForType(type, filters)));
		this.type$ = this.store.select('offers', 'type');
		this.view$ = this.store.select('offers', 'data', 'view');
		this.favorites$ = this.favoritesService.favorites$.pipe(map((list) => list.length));
		this.showDeLijn$ = this.store.select('offers').pipe(
			map((offers) => {
				if (offers.type === OffersType.ACCOMMODATION) {
					if (!offers.filters.productTypes.length) {
						return true;
					}
				}

				return false;
			})
		);
		this.showKampas$ = this.store.select('offers').pipe(
			map((offers) => {
				if (offers.type === OffersType.ACCOMMODATION) {
					if (!offers.filters.productTypes.length) {
						return true;
					}

					return !!offers.filters.productTypes.find((type: ProductType) => type === ProductType.HOSTEL || type === ProductType.YOUTH_ACCOMMODATION);
				}

				return false;
			})
		);
		this.total$ = combineLatest([this.store.select('offers', 'data', 'total'), this.showKampas$]).pipe(map(([total, showKampas]) => total + showKampas));

		combineLatest([
			this.store.select('offers', 'filters').pipe(
				tap(() => this.store.next(OFFERS_ACTIONS.loadingOffers({ loading: true }))),
				debounceTime(800)
			),
		])
			.pipe(skip(1), takeUntil(this.destroyed$))
			.subscribe(() => this.getOffers(true));

		this.getOffers();
	}

	loadMore() {
		this.getOffers();
	}

	removeFilter(filter: Filtertje) {
		this.store.next(OFFERS_ACTIONS.clearSideFilterValue({ name: filter.name, value: filter.item }));
	}

	removeAllFilters() {
		this.store.next(OFFERS_ACTIONS.clearSideFilters());
	}

	selectOrder(name: string) {
		const order = this.ORDERS.find((o) => o.name === name);

		if (order) {
			this.store.next(OFFERS_ACTIONS.setOrder({ name: order.name, asc: order.asc, sort: order.sort }));
			this.getOffers();
		}
	}

	setView(view: 'list' | 'map') {
		this.store.next(OFFERS_ACTIONS.setView({ view }));

		setTimeout(() => this.getOffers(true), 300);
	}

	private activeFiltersForType(type: OffersType, filters: OffersFilters): any[] {
		switch (type) {
			case OffersType.DAYTRIP:
				return [
					...this.filtersTranslation(filters, 'insideOutside', 'INSIDE_OUTSIDE'),
					...this.filtersTranslation(filters, 'targetAudiences', 'TARGET_AUDIENCE'),
					...this.filtersTranslation(filters, 'impairments', 'ACCESSIBILITY_TYPE'),
					...this.filtersTranslation(filters, 'facilities', 'FACILITY'),
				];
			case OffersType.ACCOMMODATION:
				return [
					...this.filtersTranslation(filters, 'meals', 'MEAL_OPTION'),
					...this.filtersTranslation(filters, 'experiences', 'EXPERIENCE'),
					...this.filtersTranslation(filters, 'roomtypes', 'ROOM_TYPE'),
					...this.filtersTranslation(filters, 'facilities', 'FACILITY'),
					...this.filtersTranslation(filters, 'impairments', 'ACCESSIBILITY_TYPE'),
				];
			case OffersType.ORGANIZED_HOLIDAY:
				return [
					...this.filtersTranslation(filters, 'overnightStays', 'OVERNIGHT_STAY'),
					...this.filtersTranslation(filters, 'destinations', 'DESTINATION'),
					...this.filtersTranslation(filters, 'transportations', 'TRANSPORTATION'),
					...this.filtersTranslation(filters, 'themes', 'THEME'),
					...this.filtersTranslation(filters, 'exclusive', 'CONDITION_TYPE', 'HOME.PRE_EXCLUSIVE'),
					...this.filtersTranslation(filters, 'inclusive', 'CONDITION_TYPE'),
				];
		}
	}

	private filtersTranslation(filters: any, name: string, base: string, pre?: string): Filtertje[] {
		return filters[name].map((item: any) => {
			return {
				name,
				base,
				item,
				pre,
			};
		});
	}

	private getOffers(clear = false) {
		this.store.next(OFFERS_ACTIONS.loadingOffers({ loading: true }));

		if (clear) {
			this.store.next(OFFERS_ACTIONS.clearOffers());
		}

		this.offersService.getOffers().subscribe({
			next: (res) => {
				this.store.next(OFFERS_ACTIONS.addOffers({ offers: res.data, total: res.total }));
				this.store.next(OFFERS_ACTIONS.loadingOffers({ loading: false }));
			},
			error: (res) => {
				this.store.next(OFFERS_ACTIONS.loadingOffers({ loading: false }));
				this.toastsService.serverError(res);
			},
		});
	}
}

class Filtertje {
	base: string;
	item: string;
	name: string;
	pre?: string;
}
